// TabConfigEditor.jsx
// Monaco + ajv + live-preview editor for capture-tab JSON configs.
// Build order step 4 (see GenericCaptureRenderer.jsx header comment).
// Left pane: Monaco JSON editor. Right pane: live GenericCaptureRenderer
// preview fed the parsed JSON, running in previewMode so it never touches
// real SharePoint data (see useSummitSync.js: a falsy storageKey short-
// circuits load/save into pure local state).
import { useState, useRef, useCallback } from "react";
import Editor from "@monaco-editor/react";
import Ajv from "ajv";
import GenericCaptureRenderer from "./GenericCaptureRenderer";
import schema from "./tabConfigs/capture-tab-schema.json";
import { BLOCK_STARTERS, BLOCK_TYPE_LABELS, BLANK_TAB_CONFIG } from "./tabConfigStarters";

const C = {
  dark: "#1a1a2e", gold: "#c9a84c", white: "#ffffff",
  border: "#e5e7eb", muted: "#6b7280", text: "#374151",
  green: "#27ae60", orange: "#e67e22", red: "#c0392b",
  blue: "#2980b9", light: "#f8f7f4"
};

const ajv = new Ajv({ allErrors: true, strict: false });
const validateTabConfig = ajv.compile(schema);

// authFetch: AdminTab passes a fetch that carries the admin's bearer token
// (notify-server's PUT /api/projects/:id is admin-only); plain fetch is only
// the fallback for previews/tests.
export default function TabConfigEditor({ project, tabId, onClose, onSaved, authFetch = fetch }) {
  const existing = project && project.tabConfigs ? project.tabConfigs[tabId] : null;
  const initialConfig = existing || Object.assign({}, BLANK_TAB_CONFIG, { id: tabId || BLANK_TAB_CONFIG.id, storageKey: tabId || BLANK_TAB_CONFIG.storageKey });
  const [text, setText] = useState(JSON.stringify(initialConfig, null, 2));
  const [errors, setErrors] = useState([]);
  const [parsedConfig, setParsedConfig] = useState(initialConfig);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const debounceRef = useRef(null);

  const applyMarkers = useCallback(function (errs) {
    if (!editorRef.current || !monacoRef.current) return;
    const model = editorRef.current.getModel();
    if (!model) return;
    const markers = errs.length ? [{
      severity: monacoRef.current.MarkerSeverity.Error,
      message: errs.map(function (e) { return e.message; }).join("\n"),
      startLineNumber: 1, startColumn: 1, endLineNumber: 1, endColumn: 1
    }] : [];
    monacoRef.current.editor.setModelMarkers(model, "ajv", markers);
  }, []);

  const validate = useCallback(function (jsonText) {
    let parsed;
    try {
      parsed = JSON.parse(jsonText);
    } catch (e) {
      const errs = [{ message: "JSON parse error: " + e.message }];
      setErrors(errs);
      setParsedConfig(null);
      applyMarkers(errs);
      return;
    }
    const valid = validateTabConfig(parsed);
    if (!valid) {
      const errs = (validateTabConfig.errors || []).map(function (e) {
        return { message: (e.instancePath || "(root)") + " " + e.message };
      });
      setErrors(errs);
      setParsedConfig(null);
      applyMarkers(errs);
    } else {
      setErrors([]);
      setParsedConfig(parsed);
      applyMarkers([]);
    }
  }, [applyMarkers]);

  function handleChange(value) {
    setText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(function () { validate(value); }, 300);
  }

  function handleEditorMount(editor, monacoInstance) {
    editorRef.current = editor;
    monacoRef.current = monacoInstance;
    validate(text);
  }

  function loadStarter(blockType) {
    const cfg = Object.assign({}, BLANK_TAB_CONFIG, {
      id: tabId || BLANK_TAB_CONFIG.id,
      name: BLOCK_TYPE_LABELS[blockType] + " Starter",
      storageKey: tabId || "newCaptureTab",
      blocks: [BLOCK_STARTERS[blockType]]
    });
    const str = JSON.stringify(cfg, null, 2);
    setText(str);
    validate(str);
  }

  async function handleSave() {
    if (errors.length || !parsedConfig) return;
    setSaving(true);
    setSaveMsg("");
    try {
      const currentTabConfigs = (project && project.tabConfigs) || {};
      const key = parsedConfig.id || tabId;
      const updatedTabConfigs = Object.assign({}, currentTabConfigs);
      updatedTabConfigs[key] = parsedConfig;
      const res = await authFetch("/api/projects/" + project.id, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tabConfigs: updatedTabConfigs })
      });
      if (!res.ok) throw new Error("Save failed: " + res.status);
      const updated = await res.json();
      setSaveMsg("Saved.");
      if (onSaved) onSaved(updated);
    } catch (e) {
      setSaveMsg("Error: " + e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: C.white, width: "94vw", height: "90vh", borderRadius: 8, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "12px 16px", background: C.dark, color: C.white, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Tab Config Editor {tabId ? ("\u2014 " + tabId) : "\u2014 new tab"}</div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <select
              onChange={function (e) { if (e.target.value) { loadStarter(e.target.value); e.target.value = ""; } }}
              style={{ fontSize: 12, padding: "4px 8px", borderRadius: 4 }}
              defaultValue=""
            >
              <option value="" disabled>Load starter template...</option>
              {Object.keys(BLOCK_TYPE_LABELS).map(function (k) {
                return <option key={k} value={k}>{BLOCK_TYPE_LABELS[k]}</option>;
              })}
            </select>
            <button
              onClick={handleSave}
              disabled={saving || errors.length > 0}
              style={{ padding: "6px 16px", background: errors.length ? C.muted : C.gold, color: C.dark, border: "none", borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: errors.length ? "not-allowed" : "pointer" }}
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button onClick={onClose} style={{ padding: "6px 12px", background: "transparent", color: C.white, border: "1px solid " + C.white, borderRadius: 6, fontSize: 12, cursor: "pointer" }}>Close</button>
          </div>
        </div>
        <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
          <div style={{ width: "50%", display: "flex", flexDirection: "column", borderRight: "1px solid " + C.border, minHeight: 0 }}>
            <div style={{ flex: 1, minHeight: 0 }}>
              <Editor
                defaultLanguage="json"
                theme="vs-dark"
                value={text}
                onChange={handleChange}
                onMount={handleEditorMount}
                options={{ minimap: { enabled: false }, fontSize: 12, scrollBeyondLastLine: false }}
              />
            </div>
            {errors.length > 0 && (
              <div style={{ background: "#fef2f2", borderTop: "1px solid " + C.red, padding: "8px 12px", maxHeight: 140, overflowY: "auto", flexShrink: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.red, marginBottom: 4 }}>{errors.length} validation error{errors.length !== 1 ? "s" : ""}</div>
                {errors.slice(0, 20).map(function (e, i) {
                  return <div key={i} style={{ fontSize: 11, color: C.red, marginBottom: 2 }}>{e.message}</div>;
                })}
              </div>
            )}
            {saveMsg && (
              <div style={{ padding: "6px 12px", fontSize: 11, color: saveMsg.indexOf("Error") === 0 ? C.red : C.green, flexShrink: 0 }}>{saveMsg}</div>
            )}
          </div>
          <div style={{ width: "50%", overflowY: "auto", padding: 16, background: C.light }}>
            {parsedConfig ? (
              <GenericCaptureRenderer tabConfig={parsedConfig} projectConfig={project} previewMode={true} />
            ) : (
              <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 12 }}>Fix validation errors to see a live preview.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
